<template>
    <aside id="no-map-sidebar" class="generic-sub-sidebar">
        <div class="gradient-pre-header"></div>
        <div class="header">
            <div class="title d-flex flex-row align-items-center mb-4">
                <icon class="mr-2" name="layer-group" color="var(--secondary)" scale="0.75" />
                <p class="text-16">{{ $t('keyword174') }}</p>
            </div>
            <SelectFloorplanInput
                class="mb-2"
                :allOptionEnabled="false"
                :project="project"
                :currentFloorplan="selectedFloorplan"
                :datesToFilter="selectedDatesArray"
                v-model="floorplanInputValue"
            />
            <!-- Floorplan Canvas -->
            <div class="floorplan-img-container">
                <FloorplanCanvas
                    ref="zoomer"
                    v-if="selectedFloorplan.url"
                    :floorplan="selectedFloorplan.url"
                    :viewpoints="selectedFloorplan.viewpoints"
                    :viewpointSize="getViewpointsSize()"
                    :grayscaleOnViewpointSelected="false"
                    :selectedViewpoint="currentViewpoint.id"
                    :enableViewpointNameTooltip="true"
                    :hoveredViewpoint="hoveredViewpoint.id"
                    :datesToFilter="selectedDatesArray"
                    @viewpointClick="viewpointClicked"
                    @viewpointHover="viewpointHover"
                />
            </div>
            <div class="floorplan-controls mb-2">
                <div class="section-a">
                    <div @click="resetZoomer" class="control-btn d-flex justify-content-center align-items-center">
                        <icon name="arrows-alt-h" scale=".75" color="white" />
                    </div>
                    <div @click="zoomOut" class="control-btn d-flex justify-content-center align-items-center">
                        <icon name="search-minus" scale=".75" color="white" />
                    </div>
                </div>
                <div class="section-b">
                    <div @click="$emit('openFloorplanFullscreen')" class="fullscreen-btn d-flex justify-content-center align-items-center">
                        <icon name="expand" color="white" />
                    </div>
                </div>
                <div class="section-c">
                    <div @click="zoomIn" class="control-btn d-flex justify-content-center align-items-center">
                        <icon name="search-plus" scale=".75" color="white" />
                    </div>
                    <div @click="resizeViewpoints" class="control-btn d-flex justify-content-center align-items-center">
                        <icon v-if="getViewpointsSize() == 1" name="ci-resize-circle" color="white" scale=".75" />
                        <icon v-if="getViewpointsSize() == 2" name="ci-resize-circle-plus" color="white" scale="1.25" />
                        <icon v-if="getViewpointsSize() == 3" name="ci-resize-circle-plus-plus" color="white" scale="1.75" />
                    </div>
                </div>
            </div>

            <div class="d-flex align-items-center justify-content-between">
                <div class="search-input-container mr-2">
                    <icon class="search-icon" name="search" color="var(--secondary-3)" scale=".5" />
                    <input v-model="searchText" class="search-input" :placeholder="$t('keyword167')" />
                </div>
                <div class="dates-filter-container">
                    <ci-button
                        @click="isDatesFilterOpen = !isDatesFilterOpen"
                        :centerIcon="{ name: 'ci-calendar', color: selectedDatesArray.length > 0 ? 'var(--secondary)' : 'white' }"
                        type="primary-3"
                        :class="{ 'filter-active': selectedDatesArray.length > 0 }"
                    />
                    <div v-show="isDatesFilterOpen">
                        <ci-filter-date-select class="dates-filter" :dates="project.capture_dates" v-model="selectedDatesArray" />
                    </div>
                </div>
            </div>
        </div>
        <div class="content d-flex flex-column">
            <div class="gradient-divider"></div>
            <vue-custom-scrollbar ref="scroll">
                <ul class="list mt-2">
                    <p class="text-12 text-weight-bold mb-1 text-center" v-if="filteredViewpoints.filterTrue.length == 0">
                        {{ $t('Components_Issues_IssueList_19') }}
                    </p>
                    <!-- VIEWPOINTS FILTER TRUE -->
                    <li
                        @click="selectViewpoint(viewpoint)"
                        v-for="(viewpoint, index) in filteredViewpoints.filterTrue"
                        :key="index"
                        @mouseenter="viewpointHover(viewpoint)"
                        @mouseleave="viewpointHover(-1)"
                        class="list-item d-flex flex-row align-items-center"
                        :class="{ 'active-item': viewpoint.id == currentViewpoint.id }"
                    >
                        <icon class="mr-2" name="ci-circle" scale=".5" />
                        <p class="text-12">{{ viewpoint.name }}</p>
                    </li>
                    <div class="list-divider"></div>
                    <!-- VIEWPOINTS FILTER FALSE -->
                    <li
                        @click="selectViewpoint(viewpoint)"
                        v-for="(viewpoint, index) in filteredViewpoints.filterFalse"
                        :key="'filter-false-' + index"
                        @mouseenter="viewpointHover(viewpoint)"
                        @mouseleave="viewpointHover(-1)"
                        class="list-item filter-false-item d-flex flex-row align-items-center"
                        :class="{ 'active-item': viewpoint.id == currentViewpoint.id }"
                    >
                        <icon class="mr-2" name="ci-circle" scale=".5" />
                        <p class="text-12">{{ viewpoint.name }}</p>
                    </li>
                </ul>
            </vue-custom-scrollbar>
        </div>
    </aside>
</template>

<script>
// ====================
// Icons
// ====================
import 'vue-awesome/icons/layer-group'
import 'vue-awesome/icons/search-plus'
import 'vue-awesome/icons/search-minus'
import 'vue-awesome/icons/arrows-alt-h'
import 'vue-awesome/icons/expand'

// ====================
// UI Components
// ====================
import FloorplanCanvas from '@/components/FloorplanCanvas'
import VueCustomScrollbar from 'vue-custom-scrollbar'
import SelectFloorplanInput from '@/components/360Viewer/Sidebar/Utils/SelectFloorplanInput'

// Vuex
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    props: {
        /**
         * determines if this component is open
         */
        isOpen: {
            type: Boolean,
            default: false,
        },
        /**
         * determines if subsidebar headers are showing or not (when mouse is hovering)
         */
        isShowingSubSidebarHeaders: {
            type: Boolean,
            default: false,
        },
        /**
         * floorplan that user selected (navigation purposes)
         */
        selectedFloorplan: {
            type: Object,
            default: () => {
                {
                }
            },
        },
        /**
         * viewpoint that user selected (navigation purposes)
         */
        selectedViewpoint: {
            type: Object,
        },
        /**
         * current floorplan of the showing viewpoint
         */
        currentFloorplan: {
            type: Object,
        },
        /**
         * current viewpoint that is showing
         */
        currentViewpoint: {
            type: Object,
        },
        /**
         * the entire project
         */
        project: {
            type: Object,
        },
    },
    components: {
        FloorplanCanvas,
        VueCustomScrollbar,
        SelectFloorplanInput,
    },
    data() {
        return {
            floorplanInputValue: '',
            searchText: '',
            filteredViewpoints: {
                filterTrue: [],
                filterFalse: [],
            },
            isDatesFilterOpen: false,
            selectedDatesArray: [],
            hoveredViewpoint: -1,
        }
    },
    computed: {
        ...mapState(['dateFilterViewer']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
        ...mapGetters('viewpointsSize', ['getById']),
    },
    watch: {
        dateFilterViewer: {
            handler() {
                this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
            },
            deep: true,
        },
        floorplanInputValue: {
            handler() {
                let fpId = this.floorplanInputValue.replace('floorplan-', '')
                let fp = this.project.floorplans.find((fp) => {
                    return fp.id == fpId
                })
                this.$emit('floorplanChange', fp)
            },
            deep: true,
        },
        selectedFloorplan: {
            handler() {
                this.filterViewpoints()
                this.$emit('changeSelectedFloorplan', this.selectedFloorplan)
            },
            deep: true,
        },
        searchText() {
            // Viewpoints
            this.filterViewpoints()
        },
        selectedDatesArray() {
            this.setDates({ projectId: this.project.id, dates: this.selectedDatesArray })
            // Viewpoints
            this.filterViewpoints()
        },
    },
    methods: {
        ...mapActions('dateFilterViewer', ['setDates']),
        ...mapActions('viewpointsSize', ['addFloorplan']),
        scrollToTop() {
            this.$refs.scroll.$el.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            })
        },
        selectViewpoint(viewpoint) {
            this.$emit('viewpointClick', viewpoint)
        },
        viewpointHover(viewpoint) {
            this.hoveredViewpoint = viewpoint
        },
        // FloorplanCanvas events
        viewpointClicked(data) {
            let currentVp = null
            this.selectedFloorplan.viewpoints.forEach((vp) => {
                if (vp.id == data.id) {
                    currentVp = vp
                }
            })
            this.selectViewpoint(currentVp)
        },
        // Controles do Floorplan
        resetZoomer() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.resetScale()
        },
        zoomIn() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.zoomIn()
        },
        zoomOut() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.zoomOut()
        },
        resizeViewpoints() {
            if (this.getViewpointsSize() < 3) {
                this.updateViewpointsSize(this.getViewpointsSize() + 1)
            } else {
                this.updateViewpointsSize(1)
            }
        },
        getViewpointsSize() {
            let projectSizeInfo = this.getById(this.project.id)

            if (projectSizeInfo) {
                let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.selectedFloorplan.id)
                if (fp) {
                    return fp.size
                } else {
                    this.updateViewpointsSize()
                }
            } else {
                this.updateViewpointsSize()
            }

            return 2
        },
        updateViewpointsSize(vpSize = 2) {
            this.addFloorplan({ project_id: this.project.id, id: this.selectedFloorplan.id, size: vpSize })
        },
        // Filtros
        filterViewpoints() {
            this.filteredViewpoints.filterTrue = []
            this.filteredViewpoints.filterFalse = []
            this.selectedFloorplan.viewpoints.forEach((viewpoint) => {
                if (viewpoint.name.toLowerCase().includes(this.searchText.toLowerCase()) && this.checkViewpointDate(viewpoint)) {
                    this.filteredViewpoints.filterTrue.push(viewpoint)
                } else {
                    this.filteredViewpoints.filterFalse.push(viewpoint)
                }
            })
            this.scrollToTop()
        },
        checkViewpointDate(viewpoint) {
            if (this.selectedDatesArray.length == 0) return true
            let flag = false
            viewpoint.images.forEach((img) => {
                let imgDate = img.date.split(' ')[0]
                this.selectedDatesArray.forEach((date) => {
                    if (date == imgDate) {
                        flag = true
                    }
                })
            })
            return flag
        },
    },
    mounted() {
        this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
        if (this.selectedFloorplan.viewpoints) {
            this.filteredViewpoints.filterTrue = this.selectedFloorplan.viewpoints
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/generic-sub-sidebar.scss';
@import '@/scss/components/360Viewer/Sidebar/Floorplans/no-map-sidebar.scss';
</style>
